import React, { useState } from 'react';
import {
  SpaceBetween,
  Button,
  Icon,
  Link,
  Box,
  Modal,
  Spinner,
  Container,
} from '@cloudscape-design/components';
import { isImageExtension } from 'src/contstant/fileConstants';

/**
 * File metadata structure for display
 */
export interface FileMetadata {
  fileName: string;
  mediaId: string;
  presignedUrl?: string;
  extension?: string;
}

/**
 * Props for FileDisplay component
 */
interface FileDisplayProps {
  /** Array of file metadata to display */
  files: FileMetadata[];
  /** Whether the component is in readonly mode */
  readonly?: boolean;
  /** Callback when remove button is clicked (edit mode only) */
  onRemove?: (mediaId: string) => void;
}

// Supported file types that can be previewed in the viewer
// Note: .mov files are not included as they have limited browser support (mainly Safari/Mac)
// PPT files removed due to limited Google Docs Viewer support
const SUPPORTED_PREVIEW_EXTENSIONS = ['.pdf', '.doc', '.docx', '.mp4', '.webm'];

// Video file extensions (only those with broad browser support)
const VIDEO_EXTENSIONS = ['.mp4', '.webm'];

/**
 * Helper to check if a file is an image based on extension
 * Images are embedded in content, so we filter them out from document display
 */
const isImageFile = (fileName: string): boolean => {
  const extension = fileName.substring(fileName.lastIndexOf('.'));
  return isImageExtension(extension);
};

/**
 * Check if file can be previewed in the viewer
 */
const canPreviewFile = (fileName: string): boolean => {
  const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
  return SUPPORTED_PREVIEW_EXTENSIONS.includes(extension);
};

/**
 * Check if file is a video
 */
const isVideoFile = (fileName: string): boolean => {
  const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
  return VIDEO_EXTENSIONS.includes(extension);
};

/**
 * FileDisplay Component
 *
 * Displays a list of non-image files using Cloudscape components.
 * Images are filtered out as they are embedded in the content.
 * In edit mode, shows a close button to remove files.
 * In readonly mode, displays files as downloadable links with attachment icon.
 */
const FileDisplay: React.FC<FileDisplayProps> = ({
  files,
  readonly = false,
  onRemove,
}) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileMetadata | null>(null);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [loadAttempted, setLoadAttempted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Filter out image files - they're already embedded in content
  const documentFiles = files.filter((file) => !isImageFile(file.fileName));

  if (!documentFiles || documentFiles.length === 0) {
    return null;
  }

  const handleViewFile = (file: FileMetadata) => {
    if (file.presignedUrl) {
      setSelectedFile(file);
      setViewerError(null);
      setLoadAttempted(false);
      setIsLoading(true);
      setViewerOpen(true);
      console.log(
        'Opening document viewer for:',
        file.fileName,
        'URL:',
        file.presignedUrl,
      );
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedFile(null);
    setViewerError(null);
    setLoadAttempted(false);
    setIsLoading(true);
  };

  const handleIframeLoad = () => {
    setLoadAttempted(true);
    setIsLoading(false);
  };

  const handleIframeError = () => {
    console.error('Failed to load document in iframe');
    setViewerError('Unable to preview this document.');
    setLoadAttempted(true);
  };

  // Get the appropriate viewer URL
  const getViewerUrl = (file: FileMetadata): string => {
    if (!file.presignedUrl) return '';

    const extension = file.fileName
      .substring(file.fileName.lastIndexOf('.'))
      .toLowerCase();

    // Use Google Docs Viewer for Office documents
    if (['.doc', '.docx'].includes(extension)) {
      return `https://docs.google.com/viewer?url=${encodeURIComponent(file.presignedUrl)}&embedded=true`;
    }

    // Use direct URL for PDFs, videos, and other supported files
    return file.presignedUrl;
  };

  return (
    <>
      <SpaceBetween direction="horizontal" size="xs">
        {documentFiles.map((file) => (
          <Box key={file.mediaId} padding={{ vertical: 'xxxs' }}>
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Icon name="file" />
              {file.presignedUrl ? (
                <>
                  {canPreviewFile(file.fileName) ? (
                    <Button
                      variant="inline-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFile(file);
                      }}
                    >
                      {file.fileName}
                    </Button>
                  ) : (
                    <Link
                      href={file.presignedUrl}
                      external
                      target="_blank"
                      fontSize="body-m"
                    >
                      {file.fileName}
                    </Link>
                  )}
                  {canPreviewFile(file.fileName) && (
                    <Link
                      href={file.presignedUrl}
                      external
                      target="_blank"
                      fontSize="body-s"
                      ariaLabel={`Download ${file.fileName}`}
                    />
                  )}
                </>
              ) : (
                <Box fontSize="body-m" color="text-body-secondary">
                  {file.fileName}
                </Box>
              )}
              {!readonly && onRemove && (
                <Button
                  variant="icon"
                  iconName="close"
                  onClick={() => onRemove(file.mediaId)}
                  ariaLabel={`Remove ${file.fileName}`}
                />
              )}
            </SpaceBetween>
          </Box>
        ))}
      </SpaceBetween>

      {/* Document Viewer Modal */}
      <Modal
        visible={viewerOpen}
        onDismiss={handleCloseViewer}
        header={selectedFile?.fileName || 'Document Viewer'}
        size="max"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={handleCloseViewer}>Close</Button>
              <Button
                variant="primary"
                href={selectedFile?.presignedUrl}
                target="_blank"
                iconName="external"
              >
                Open in New Tab
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {viewerError ? (
          <Box padding="l" textAlign="center">
            <SpaceBetween size="m">
              <Box color="text-status-error" fontSize="heading-m">
                Error
              </Box>
              <Box color="text-body-secondary">{viewerError}</Box>
              <Button
                variant="primary"
                href={selectedFile?.presignedUrl}
                target="_blank"
                iconName="external"
              >
                Download or Open in New Tab
              </Button>
            </SpaceBetween>
          </Box>
        ) : selectedFile && selectedFile.presignedUrl ? (
          canPreviewFile(selectedFile.fileName) ? (
            // Iframe for all supported files (documents and videos)
            <Container fitHeight disableContentPaddings>
              <div className="document-viewer-container">
                {isLoading && (
                  <SpaceBetween size="m" alignItems="center">
                    <Spinner size="large" />
                    <Box fontSize="heading-m" color="text-body-secondary">
                      Loading
                    </Box>
                  </SpaceBetween>
                )}
                <iframe
                  src={getViewerUrl(selectedFile)}
                  title={selectedFile.fileName}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                  style={{ display: isLoading ? 'none' : 'block' }}
                />
              </div>
            </Container>
          ) : (
            // Files that cannot be previewed - show download option
            <Box padding="l" textAlign="center">
              <SpaceBetween size="l">
                <Icon name="file" size="big" />
                <Box fontSize="heading-m">{selectedFile.fileName}</Box>
                <Box color="text-body-secondary">
                  This file type cannot be previewed in the browser.
                </Box>
                <Box color="text-body-secondary" fontSize="body-s">
                  Supported preview formats: PDF, DOC, DOCX, MP4, WebM
                </Box>
                <Box color="text-body-secondary" fontSize="body-s">
                  Note: MOV and XLSX files require download due to limited browser support
                </Box>
                <Button
                  variant="primary"
                  href={selectedFile.presignedUrl}
                  target="_blank"
                  iconName="download"
                >
                  Download File
                </Button>
              </SpaceBetween>
            </Box>
          )
        ) : null}
      </Modal>
    </>
  );
};

export default FileDisplay;
